var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Coords } from '../../model/Coords';
import { loadMaplibre, MAPLIBRE_CSS_URL } from './maplibre.js';
let BasicMap = class BasicMap extends LitElement {
    constructor() {
        super(...arguments);
        this.mapOptions = {};
        this.mapLib = null;
    }
    render() {
        const styleUrl = this.mapOptions.mapboxApiKey
            ? 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css'
            : MAPLIBRE_CSS_URL;
        const containerStyle = this.mapOptions.skyColor
            ? `background-color: ${this.mapOptions.skyColor};`
            : '';
        return html `
    <link
      href="${styleUrl}"
      rel="stylesheet"
    />
    <div id="map" style=${containerStyle}>
        <slot></slot>
    </div>`;
    }
    /**
     * Initialisation après rendu du DOM
     */
    async firstUpdated() {
        const container = this.renderRoot.querySelector('#map');
        const useMapbox = Boolean(this.mapOptions.mapboxApiKey);
        // Import dynamique de la bonne lib
        if (useMapbox) {
            this.mapLib = (await import('mapbox-gl')).default;
            this.mapLib.accessToken = this.mapOptions.mapboxApiKey;
        }
        else {
            this.mapLib = await loadMaplibre();
        }
        // Définition automatique d’un style par défaut
        const style = this.mapOptions.style ??
            (useMapbox
                ? 'mapbox://styles/mapbox/streets-v11'
                : 'https://demotiles.maplibre.org/style.json');
        const { terrain: _terrain, ...passthroughOptions } = this.mapOptions;
        const options = {
            container,
            center: this.mapOptions.center ?? [2.3522, 48.8566], // par défaut Paris
            zoom: this.mapOptions.zoom ?? 4,
            pitch: this.mapOptions.pitch ?? 0,
            bearing: this.mapOptions.bearing ?? 0,
            interactive: this.mapOptions.interactive ?? true,
            style,
            ...passthroughOptions,
            // On désactive le contrôle d'attribution par défaut (coin bas-droit)
            // pour le replacer en bas à gauche juste après — voir addControl plus
            // bas. Placé après le spread pour primer sur d'éventuelles options.
            attributionControl: false
        };
        this.map = new this.mapLib.Map(options);
        // Crédits Mapbox / MapLibre en bas à gauche (le coin bas-droit est
        // réservé aux overlays applicatifs, ex. la légende des réseaux).
        if (this.mapLib.AttributionControl) {
            this.map.addControl(new this.mapLib.AttributionControl(), 'bottom-left');
        }
        // MapLibre v5: projection is not a constructor option, use setProjection()
        if (!useMapbox && this.mapOptions.projection && typeof this.map.setProjection === 'function') {
            const proj = typeof this.mapOptions.projection === 'string'
                ? { type: this.mapOptions.projection }
                : this.mapOptions.projection;
            this.map.once('style.load', () => {
                this.map.setProjection(proj);
            });
        }
        this.map.on('load', () => {
            const skyColor = this.mapOptions.skyColor;
            if (skyColor && typeof this.map.setSky === 'function') {
                this.map.setSky({
                    'sky-color': skyColor,
                    'horizon-color': skyColor,
                    'fog-color': skyColor,
                    'sky-horizon-blend': 0.8,
                    'horizon-fog-blend': 0.8,
                    'fog-ground-blend': 0.5,
                });
            }
            const exaggeration = this.resolveTerrainExaggeration(this.mapOptions.terrain);
            if (exaggeration > 0 && typeof this.map.setTerrain === 'function') {
                const TERRAIN_SOURCE = 'aws-terrarium-dem';
                if (!this.map.getSource(TERRAIN_SOURCE)) {
                    this.map.addSource(TERRAIN_SOURCE, {
                        type: 'raster-dem',
                        tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
                        tileSize: 256,
                        encoding: 'terrarium',
                        maxzoom: 15,
                    });
                }
                this.map.setTerrain({ source: TERRAIN_SOURCE, exaggeration });
            }
            this.dispatchEvent(new CustomEvent('map-loaded', { detail: {
                    map: this.map,
                    mapLib: this.mapLib,
                } }));
            this.map.on('click', (e) => {
                this.dispatchEvent(new CustomEvent('map-click', {
                    detail: { coords: new Coords(e.lngLat.lng, e.lngLat.lat) },
                    bubbles: true,
                    composed: true,
                }));
            });
        });
    }
    resolveTerrainExaggeration(value) {
        if (value === true)
            return 1;
        if (typeof value === 'number' && value > 0)
            return value;
        return 0;
    }
    getMapInstance() {
        return this.map;
    }
    getMapLib() {
        return this.mapLib;
    }
};
BasicMap.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    #map {
      width: 100%;
      height: 100%;
    }
  `;
__decorate([
    property({ type: Object })
], BasicMap.prototype, "mapOptions", void 0);
__decorate([
    state()
], BasicMap.prototype, "map", void 0);
BasicMap = __decorate([
    customElement('basic-map')
], BasicMap);
export { BasicMap };
//# sourceMappingURL=BasicMap.js.map